import { loadStripe } from "@stripe/stripe-js";
import { api } from "@/lib/api";

/** Liste cartes alignées sur le PaymentIntent de la transaction (checkout). */
export async function prefetchPaymentMethodsForTransaction(transactionId) {
  if (!transactionId) return [];
  try {
    const res = await api.get("/payment-methods/list", {
      params: { light: 1, transaction_id: String(transactionId) },
    });
    const body = res?.data && typeof res.data === "object" ? res.data : {};
    return Array.isArray(body.payment_methods) ? body.payment_methods : [];
  } catch {
    return [];
  }
}

/**
 * Crée la commande puis charge les cartes du même client Stripe que le PI.
 * @returns {Promise<{ data: object, initialPaymentMethods: object[] }>}
 */
export async function postDossierBuyParallel(dossierId, params = {}) {
  const buyRes = await api.post(`/dossiers/${dossierId}/buy`, null, { params });
  const data = buyRes?.data;
  const tx = data?.transactionId || data?.transaction_id;
  const needsModal = data?.checkout === "payment_element" && (data?.clientSecret || data?.client_secret);
  const initialPaymentMethods = needsModal && tx ? await prefetchPaymentMethodsForTransaction(tx) : [];
  return { data, initialPaymentMethods };
}

function publishableKey() {
  return (
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
    process.env.REACT_APP_STRIPE_PUBLISHABLE ||
    ""
  );
}

/**
 * Réponse /api/acheter :
 * - checkout "authorized" : carte enregistrée, pré-autorisation OK (séquestre)
 * - checkout "requires_action" : 3DS court via confirmCardPayment
 * - checkout "payment_element" : ouvrir la modale Stripe complète
 *
 * @returns {"done"|"modal"|"error"}
 */
export async function handleAcheterResponse(data, { toast, onOpenPaymentDialog, initialPaymentMethods }) {
  const tx = data?.transactionId || data?.transaction_id;

  if (data?.checkout === "authorized") {
    toast.success(
      "Séquestre activé sur votre carte. Ouvrez le tableau de bord → onglet « Mes achats » pour appeler et suivre ce RDV.",
      { duration: 7000 },
    );
    return "done";
  }

  if (data?.checkout === "requires_action" && data?.clientSecret) {
    const key = publishableKey();
    if (!key) {
      toast.error("Configuration Stripe manquante.");
      return "error";
    }
    const stripe = await loadStripe(key);
    if (!stripe) {
      toast.error("Stripe indisponible.");
      return "error";
    }
    const { error, paymentIntent } = await stripe.confirmCardPayment(String(data.clientSecret), {
      return_url: typeof window !== "undefined" ? window.location.href : undefined,
    });
    if (error) {
      toast.error(error.message || "Authentification du paiement refusée.");
      return "error";
    }
    const st = paymentIntent?.status;
    if (st === "requires_capture" || st === "succeeded" || st === "processing") {
      if (tx && st === "requires_capture") {
        try {
          await api.post("/payments/confirm", { transaction_id: String(tx) });
        } catch (syncErr) {
          toast.error(
            syncErr?.message ||
              "Paiement validé par Stripe mais synchronisation impossible. Réessayez depuis « Mes achats ».",
          );
          return "error";
        }
      }
      toast.success(
        "Séquestre confirmé. Tableau de bord → « Mes achats » pour lancer l’appel.",
        { duration: 7000 },
      );
      return "done";
    }
    toast.error("Statut de paiement inattendu.");
    return "error";
  }

  const cs = data?.clientSecret || data?.client_secret;
  if (cs && tx) {
    onOpenPaymentDialog({
      clientSecret: String(cs),
      transactionId: String(tx),
      initialPaymentMethods,
    });
    return "modal";
  }

  toast.error("Paiement indisponible (réponse serveur incomplète).");
  return "error";
}
